<template>
    <div class="flex w-full flex-col gap-5">
        <Uploader
            ref="uploader"
            v-slot="{ openDialog, status, reset }"
            v-model="files"
            :url="url"
            :type="type"
            :scope="scope"
            :multiple="multiple"
            :useBatchUpload="useBatchUpload"
            :accept="accept"
            :capture="capture"
            :androidAllowCamera="androidAllowCamera"
        >
            <div
                v-if="hasCollection"
                class="form-row max-sm:w-full"
            >
                <div class="flex flex-col gap-4">
                    <div class="form-titled-item">
                        <span class="text-sm font-medium">Document</span>
                        <MuvalSelect
                            vid="collection_name"
                            placeholder="Choose a document to upload"
                            singleSelect
                            displayLabel="label"
                            :selectOptions="collectionOptions"
                            class="w-full lg:w-64"
                        />
                    </div>
                    <MuvalSelect
                        v-if="values.collection_name == 'SUPPORT'"
                        vid="upload_info_type"
                        :selectOptions="supportUploadInfoTypes"
                        displayLabel="label"
                        valueProp="value"
                        label="Upload type"
                    />
                    <MuvalSelect
                        v-if="values.collection_name == 'STORAGE'"
                        vid="upload_info_type"
                        :selectOptions="storageUploadInfoTypes"
                        displayLabel="label"
                        valueProp="value"
                        label="Upload type"
                    />
                    <MuvalTextArea
                        v-if="values.upload_info_type == 'OTHER'"
                        vid="upload_info_other"
                        label="Other please specify a short description"
                    />
                </div>
                <div
                    v-if="collectionOptions?.find((el) => el.id == values.collection_name)?.expiry_date"
                    class="form-titled-item"
                >
                    <span class="text-sm font-medium">Expiry date</span>
                    <MPDatetime
                        v-model="documentExpiry"
                        vid="expiry_date"
                        :min-date="new Date()"
                    />
                </div>
                <MPDatetime
                    v-if="collectionOptions?.find((el) => el.id == values.collection_name)?.check_date"
                    v-model="checkDate"
                    label="Check date"
                    :max-date="new Date()"
                />
                <MuvalInput
                    v-if="collectionOptions?.find((el) => el.id == values.collection_name)?.credit_score"
                    v-model="creditScore"
                    label="Credit score"
                    class="w-32!"
                />
            </div>
            <DocumentExpirySetup
                v-if="entity === 'staff'"
                v-model="expiryDraft"
                :expiry="documentExpiry ? dayjs(documentExpiry).format('YYYY-MM-DD') : ''"
                :title="`${collectionOptions?.find((el) => el.id === values.collection_name)?.label || 'Document'} expires - ${resourceName}`"
                :files="attachments || []"
            />
            <DropZone v-slot="{ hovered }">
                <div
                    class="flex h-fit w-full flex-col items-center justify-center rounded-md p-4 transition-colors duration-150"
                    :class="[
                        props.fineDashedBorder ? '' : 'border-muval-gray-4 border-2 border-dashed',
                        {
                            'border-muval-sky-1': hovered && !props.fineDashedBorder,
                            'space-y-4': files.length,
                            'space-y-2': !files.length,
                            'cursor-progress': status.isUploading,
                        },
                    ]"
                    :style="{
                        backgroundImage: props.fineDashedBorder ? getFineDashedBorderBackground(hovered) : undefined,
                    }"
                >
                    <div
                        v-if="!files.length"
                        class="text-muval-gray-1 flex flex-col items-center gap-1 text-base"
                    >
                        <span class="text-center">{{ prompt }}</span>
                        <span class="text-muval-gray-2 text-xs">{{ acceptedFileTypeLabel }}</span>
                    </div>
                    <div
                        v-else-if="!status.isUploading && !status.hasUploaded"
                        class="flex flex-wrap justify-center gap-2"
                        :class="{
                            'pointer-events-none': status.isUploading,
                        }"
                    >
                        <div
                            v-for="(file, fileIdx) in files"
                            :key="file.name"
                            class="flex cursor-default gap-2 rounded-sm border px-2 py-1 text-sm shadow-sm"
                        >
                            <svg
                                v-if="file?.type?.includes('image')"
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                class="my-auto h-4 w-4"
                            >
                                <path
                                    d="M16 2V16H2V2H16ZM16 0H2C0.9 0 0 0.9 0 2V16C0 17.1 0.9 18 2 18H16C17.1 18 18 17.1 18 16V2C18 0.9 17.1 0 16 0ZM11.14 8.86L8.14 12.73L6 10.14L3 14H15L11.14 8.86Z"
                                    fill="#808080"
                                />
                            </svg>

                            <svg
                                v-else
                                class="my-auto h-4 w-4"
                                width="16"
                                height="20"
                                viewBox="0 0 16 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M10 0H2C1.4713 0.00264352 0.965159 0.214524 0.592247 0.58931C0.219334 0.964097 0.00999362 1.4713 0.0100002 2L1.56476e-10 18C-6.6087e-06 18.5287 0.209334 19.0359 0.582246 19.4107C0.955159 19.7855 1.4613 19.9974 1.99 20H14C14.5299 19.9984 15.0377 19.7872 15.4125 19.4125C15.7872 19.0377 15.9984 18.5299 16 18V6L10 0ZM12 16H4V14H12V16ZM12 12H4V10H12V12ZM9 7V1.5L14.5 7H9Z"
                                    fill="currentColor"
                                    fill-opacity="0.5"
                                />
                            </svg>
                            <span class="whitespace-no-wrap">{{ file.name }}</span>
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                class="text-muval-gray-4 hover:text-error my-auto h-3 w-3 cursor-pointer"
                                @click.prevent="files.splice(fileIdx, 1)"
                            >
                                <path
                                    d="M13.3 0.710001C12.91 0.320001 12.28 0.320001 11.89 0.710001L6.99997 5.59L2.10997 0.700001C1.71997 0.310001 1.08997 0.310001 0.699971 0.700001C0.309971 1.09 0.309971 1.72 0.699971 2.11L5.58997 7L0.699971 11.89C0.309971 12.28 0.309971 12.91 0.699971 13.3C1.08997 13.69 1.71997 13.69 2.10997 13.3L6.99997 8.41L11.89 13.3C12.28 13.69 12.91 13.69 13.3 13.3C13.69 12.91 13.69 12.28 13.3 11.89L8.40997 7L13.3 2.11C13.68 1.73 13.68 1.09 13.3 0.710001Z"
                                    fill="currentColor"
                                />
                            </svg>

                            <!-- <File :value="file" :index="files.length > 1 ? fileIdx : null" /> -->
                        </div>
                    </div>
                    <template v-if="status.hasError">
                        <div class="flex w-full flex-col items-center gap-2">
                            <div class="flex gap-2">
                                <MuvalButton
                                    small
                                    @click.prevent="uploadHandler"
                                >
                                    Retry
                                </MuvalButton>
                                <MuvalButton
                                    small
                                    @click.prevent="reset"
                                >
                                    Reset
                                </MuvalButton>
                            </div>
                            <div class="bg-muval-red-2 text-error flex w-full flex-col rounded-md p-4">
                                <h3 class="mb-2 text-lg font-bold">Something went wrong</h3>
                                <ul
                                    v-if="uploadErrors.length"
                                    class="list-disc pl-5 leading-snug"
                                >
                                    <li
                                        v-for="(error, index) in uploadErrors"
                                        :key="index"
                                        class="mb-1"
                                    >
                                        {{ error }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </template>
                    <template v-else-if="status?.isUploading">
                        <div class="my-auto flex flex-col items-center gap-2">
                            <span class="animate-pulse">Uploading...</span>
                            <div class="bg-muval-gray-5 relative h-3 w-40 overflow-hidden rounded-full">
                                <div
                                    class="bg-brand absolute h-3 rounded-full transition-all"
                                    :style="{ width: progressPercent + '%' }"
                                ></div>
                            </div>
                        </div>
                    </template>
                    <template v-else-if="status.hasUploaded">
                        <div class="flex flex-col gap-2">
                            <span
                                class="text-success mx-auto"
                                @click.prevent=""
                            >
                                Upload successful!
                            </span>
                            <MuvalButton
                                quaternary
                                small
                                @click.prevent="
                                    () => {
                                        files = [];
                                        if (hasCollection) {
                                            values.collection_name = null;
                                            documentExpiry = null;
                                            checkDate = null;
                                            creditScore = null;
                                        }
                                        reset();
                                    }
                                "
                            >
                                Click here to upload again.
                            </MuvalButton>
                        </div>
                    </template>
                    <template v-else-if="files.length < 1">
                        <span class="text-muval-gray-1 text-lg">{{ label ?? '' }}</span>
                        <MuvalButton
                            tertiary
                            small
                            :icon="buttonIcon"
                            :icon-class="buttonIconClass"
                            :icon-color="buttonIconColor"
                            @click.prevent="openDialog"
                        >
                            {{ buttonLabel }}
                        </MuvalButton>
                    </template>
                    <template v-else-if="files.length">
                        <div class="flex gap-2">
                            <MuvalButton
                                v-if="multiple"
                                tertiary
                                small
                                :icon="buttonIcon"
                                :icon-class="buttonIconClass"
                                :icon-color="buttonIconColor"
                                @click.prevent="openDialog"
                            >
                                Add more files
                            </MuvalButton>
                            <MuvalButton
                                v-if="!hideUploadButton"
                                small
                                @click.prevent="uploadHandler"
                            >
                                Upload
                            </MuvalButton>
                        </div>
                    </template>
                </div>
            </DropZone>

            <TransitionGroup
                v-if="showAttachments"
                name="list"
                tag="div"
                class="flex flex-col gap-2 empty:hidden"
            >
                <div
                    v-for="attachment in attachments"
                    :key="attachment.id"
                    class="form-col"
                >
                    <div
                        role="group"
                        tabindex="0"
                        class="card flex w-full items-center justify-between gap-4 px-4 py-2 text-sm"
                        :class="canViewFiles ? 'cursor-pointer' : ''"
                        @click="canViewFiles && viewAttachment(attachment)"
                        @keydown.enter.self.prevent="canViewFiles && viewAttachment(attachment)"
                        @touchstart="canViewFiles && viewAttachment(attachment)"
                    >
                        <div class="my-auto flex items-center gap-4">
                            <div>
                                <svg
                                    v-if="attachment.mime_type.includes('image')"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 18 18"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M16 2V16H2V2H16ZM16 0H2C0.9 0 0 0.9 0 2V16C0 17.1 0.9 18 2 18H16C17.1 18 18 17.1 18 16V2C18 0.9 17.1 0 16 0ZM11.14 8.86L8.14 12.73L6 10.14L3 14H15L11.14 8.86Z"
                                        fill="#808080"
                                    />
                                </svg>

                                <svg
                                    v-else
                                    class="m-auto"
                                    width="16"
                                    height="20"
                                    viewBox="0 0 16 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M10 0H2C1.4713 0.00264352 0.965159 0.214524 0.592247 0.58931C0.219334 0.964097 0.00999362 1.4713 0.0100002 2L1.56476e-10 18C-6.6087e-06 18.5287 0.209334 19.0359 0.582246 19.4107C0.955159 19.7855 1.4613 19.9974 1.99 20H14C14.5299 19.9984 15.0377 19.7872 15.4125 19.4125C15.7872 19.0377 15.9984 18.5299 16 18V6L10 0ZM12 16H4V14H12V16ZM12 12H4V10H12V12ZM9 7V1.5L14.5 7H9Z"
                                        fill="currentColor"
                                        fill-opacity="0.5"
                                    />
                                </svg>
                            </div>
                            <div class="flex min-w-0 flex-col gap-1">
                                <div class="flex items-center gap-2">
                                    {{
                                        attachment.collection_name !== 'default' ?
                                            attachment.custom_properties.display_label ||
                                            collectionOptions?.find((el) => el.id == attachment.collection_name)?.label
                                        :   attachment.name
                                    }}
                                    <span
                                        v-if="attachment.custom_properties.info"
                                        class="text-muval-gray-2 text-xs"
                                    >
                                        - {{ attachment.custom_properties.info }}
                                    </span>
                                </div>
                                <div class="text-muval-gray-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                                    <span>Uploaded: {{ $dayjs(attachment.created_at).format('DD/MM/YY') }}</span>
                                    <template
                                        v-for="(field, fieldName) in customProperties"
                                        :key="fieldName"
                                    >
                                        <span v-if="attachment.custom_properties[fieldName]"
                                            >{{ field?.label }}:
                                            {{
                                                field.type === 'date' ?
                                                    $dayjs(attachment.custom_properties[fieldName]).format('DD/MM/YY')
                                                :   attachment.custom_properties[fieldName]
                                            }}</span
                                        >
                                    </template>
                                    <DocumentExpiryFile
                                        v-if="entity === 'staff'"
                                        :file="attachment"
                                    />
                                </div>
                            </div>
                        </div>
                        <div
                            class="flex shrink-0 items-center"
                            @click.stop
                            @keydown.stop
                        >
                            <ThreeDotOption
                                v-if="canBeUpdated(attachment) || canDeleteFiles"
                                aria-label="File actions"
                            >
                                <div
                                    class="absolute right-0 z-50 min-w-32 overflow-hidden rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5"
                                >
                                    <button
                                        v-if="canBeUpdated(attachment)"
                                        type="button"
                                        class="hover:bg-muval-gray-6 w-full px-4 py-2 text-left text-sm"
                                        @click.stop="showModal(attachment, 'UPDATE')"
                                    >
                                        Edit file
                                    </button>
                                    <button
                                        v-if="canDeleteFiles"
                                        type="button"
                                        class="hover:bg-muval-gray-6 w-full px-4 py-2 text-left text-sm"
                                        @click.stop="showModal(attachment, 'DELETE')"
                                    >
                                        Delete file
                                    </button>
                                </div>
                            </ThreeDotOption>
                        </div>
                    </div>
                </div>
            </TransitionGroup>

            <MuvalModal
                v-model="attachmentModal.show"
                :modalSize="attachmentModal.size"
            >
                <template #header>
                    {{ attachmentModal.title }}
                </template>
                <component
                    :is="attachmentModal.form"
                    v-if="attachmentModal.data"
                    :key="attachmentModal.data.id"
                    :data="attachmentModal.data"
                    :document-reminders="entity === 'staff'"
                    :resource-name="resourceName"
                    :url="url"
                    :type="type"
                    @cancel:modal="
                        () => {
                            attachmentModal.show = false;
                            attachmentModal.data = null;
                        }
                    "
                    @close:modal="handleSuccess($event)"
                />
            </MuvalModal>
        </Uploader>
    </div>
</template>
<script setup>
import ThreeDotOption from '@components/button/ThreeDotOption.vue';
import DocumentExpirySetup from '@components/company-diary/DocumentExpirySetup.vue';
import DocumentExpiryFile from '@components/company-diary/DocumentExpiryFile.vue';
import { createDocumentExpiry } from '@components/company-diary/documentExpiry';

import Uploader from '@components/upload/Uploader.vue';
import DropZone from '@components/upload/DropZone.vue';
import { useForm } from 'vee-validate';
import { getFineDashedBorderBackground } from '@utilities/fineDashedBorder';

import DeleteAttachmentModal from '@components/modal/templates/DeleteAttachmentModal.vue';
import UpdateAttachmentModal from '@components/modal/templates/UpdateAttachmentModal.vue';
import MuvalButton from '@buttons/MuvalButton.vue';
import MuvalSelect from '@inputs/MuvalSelect.vue';
import MuvalModal from '@components/modal/MuvalModal.vue';
import useDayJs from '@plugins/useDayJs';
// Note: Removed unused store imports (company, transport, user) as they're no longer needed

const dayjs = useDayJs();

const props = defineProps({
    resourceName: { type: String, default: '' },
    url: {
        type: String,
        required: true,
    },
    type: {
        type: String,
        required: true,
    },
    multiple: {
        type: Boolean,
        required: false,
        default: false,
    },
    hasCollection: {
        type: Boolean,
        required: false,
        default: false,
    },
    prompt: {
        type: String,
        required: false,
        default: 'Select a file to upload',
    },
    acceptedFileTypeLabel: {
        type: String,
        required: false,
        default: '',
    },
    fineDashedBorder: {
        type: Boolean,
        required: false,
        default: false,
    },
    showAttachments: {
        type: Boolean,
        required: false,
        default: false,
    },
    canDeleteFiles: {
        type: Boolean,
        required: false,
        default: false,
    },
    canViewFiles: {
        type: Boolean,
        required: false,
        default: false,
    },
    canUpdateFiles: {
        type: Boolean,
        required: false,
        default: false,
    },
    scope: {
        type: String,
        required: false,
        default: 'tenant',
    },
    isSigning: {
        type: Boolean,
        required: false,
        default: false,
    },
    isStoring: {
        type: Boolean,
        required: false,
        default: false,
    },
    buttonLabel: {
        type: String,
        required: false,
        default: 'Add file',
    },
    buttonIcon: {
        type: String,
        required: false,
        default: 'system-add',
    },
    buttonIconClass: {
        type: String,
        required: false,
        default: '',
    },
    buttonIconColor: {
        type: String,
        required: false,
        default: '',
    },
    entity: {
        type: String,
        required: false,
        default: '',
    },
    hideUploadButton: {
        type: Boolean,
        required: false,
        default: false,
    },
    additionalPayload: {
        type: Object,
        required: false,
        default: () => ({}),
    },
    // Only used for the inventory condition report images
    useBatchUpload: {
        type: Boolean,
        required: false,
        default: false,
    },
    accept: {
        type: String,
        required: false,
        default: '*/*',
    },
    capture: {
        type: String,
        required: false,
        default: null,
    },
    androidAllowCamera: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const axios = computed(() => {
    if (props.scope === 'tenant') {
        return window.tenantAxios;
    } else if (props.scope === 'crew') {
        return window.crewAxios;
    } else {
        return window.centralAxios;
    }
});

const emit = defineEmits(['uploaded', 'uploading', 'error']);

const { values, resetForm, setErrors } = useForm();

const supportUploadInfoTypes = [
    { label: 'Bank transfer advice', value: 'BANK_TRANSFER_ADVICE' },
    { label: 'Other', value: 'OTHER' },
];

const storageUploadInfoTypes = [
    { label: "Driver's licence", value: 'DRIVERS_LICENCE' },
    { label: 'Signed document', value: 'SIGNED_DOCUMENT' },
];

const files = ref([]);
const collectionOptions = ref();
const documentExpiry = ref();
const expiryDraft = ref({
    enabled: false,
    title: '',
    timezone: '',
    days_before: 30,
    time: '09:00',
    recipient_user_ids: [],
    recipient_roles: [],
    replaces_file_id: null,
});
const checkDate = ref();
const creditScore = ref();

(async () => {
    if (props.hasCollection) {
        const { data } = await axios.value.get(`${props.url}/${props.type}/collections`);
        let formatted = [];
        Object.entries(data.filetypes).forEach((item) => {
            formatted.push({ id: item[0], ...item[1] });
        });
        collectionOptions.value = formatted;
        // documentTypes.value = Object.entries(data.filetypes).map((el) => el);
    }
})();

const dataPoint = computed(() => {
    return `${props.url}/${props.type}`;
});

watch(dataPoint, async () => {
    if (props.showAttachments) {
        getAttachments();
    }

    if (props.hasCollection) {
        (async () => {
            const { data } = await axios.value.get(`${props.url}/${props.type}/collections`);
            let formatted = [];
            Object.entries(data.filetypes).forEach((item) => {
                formatted.push({ id: item[0], ...item[1] });
            });
            collectionOptions.value = formatted;
        })();
    }
});

const attachments = ref();

async function getAttachments() {
    if (props.showAttachments) {
        const {
            data: { data: resp },
        } = await axios.value.get(`${props.url}/${props.type}`);
        attachments.value = resp;
    }
}

// Note: Do not re-emit child's bare 'uploaded' event here; we emit a single
// meaningful 'uploaded' event with payload from uploadFile() after success.

onMounted(async () => {
    if (props.showAttachments) {
        const {
            data: { data: resp },
        } = await axios.value.get(`${props.url}/${props.type}`);
        attachments.value = resp;
    }
});

// check if canUpdateFiles and file has updateable properties
function canBeUpdated(attachment) {
    if (!props.canUpdateFiles || !props.hasCollection) {
        return false;
    }
    const updateableProperties = ['expiry_date'];
    const collection = collectionOptions.value?.find((el) => el.id == attachment.collection_name);
    let properties = [];
    Object.keys(customProperties.value).forEach((key) => {
        if (collection && collection[key]) {
            properties = [...properties, key];
        }
    });

    if (properties.some((property) => updateableProperties.includes(property))) {
        return true;
    }
}

const attachmentModal = reactive({
    show: false,
    size: '',
    form: null,
    data: null,
    title: '',
});

async function showModal(attachment, event) {
    attachmentModal.data = attachment;
    attachmentModal.show = true;
    if (event == 'DELETE') {
        attachmentModal.size = 'md:w-2/3';
        attachmentModal.title = 'Remove file';
        attachmentModal.form = DeleteAttachmentModal;
    } else if (event == 'UPDATE') {
        attachmentModal.size = 'md:w-1/3';
        attachmentModal.title = 'Update file';
        attachmentModal.form = UpdateAttachmentModal;
    }
}

function handleSuccess() {
    getAttachments();
    attachmentModal.show = false;
    attachmentModal.data = null;
}

const isLoading = ref(true);
async function viewAttachment(attachment) {
    // Show loading indicator
    isLoading.value = true;

    try {
        // Use the same URL pattern as the rest of the component for consistency
        const response = await axios.value.get(`${props.url}/${props.type}/${attachment.id}/view`);

        if (response.data && response.data.download_url) {
            // Open the download URL in a new tab
            window.open(response.data.download_url, '_blank');
        } else {
            throw new Error('Download URL not found in response');
        }
    } catch (error) {
        console.error('Error downloading attachment:', error);
    } finally {
        isLoading.value = false;
    }
}

const uploader = ref(null);
const progressPercent = computed(() => {
    const up = uploader.value?.uploadProgress;
    const raw = up && typeof up === 'object' && 'value' in up ? up.value : up;
    const num = Number(raw);
    return Number.isFinite(num) ? num : 0;
});
async function uploadHandler() {
    if (props.entity === 'staff' && values.collection_name === 'POLICE_CHECK' && !documentExpiry.value) {
        setErrors({ expiry_date: 'An expiry date is required for a police check.' });
        return;
    }
    if (props.hasCollection) {
        if (values.collection_name) {
            const { properties, valid } = getCustomProperties();
            if (properties) {
                // only submit when required custom properties have values
                if (valid) {
                    const payload = {
                        collection_name: values.collection_name,
                        ...properties,
                        ...props.additionalPayload,
                    };
                    uploadFile(payload);
                }
            } else {
                const payload = {
                    collection_name: values.collection_name,
                    upload_info_type: values.upload_info_type,
                    upload_info_other: values.upload_info_other,
                    ...props.additionalPayload,
                };
                uploadFile(payload, true);
            }
        }
    } else {
        uploadFile(props.additionalPayload);
    }
}

const uploadErrors = ref([]);

async function uploadFile(payload = null, reset = false) {
    const reminderDraft = JSON.parse(JSON.stringify(expiryDraft.value));
    try {
        if (uploader.value && uploader.value.uploadProgress && typeof uploader.value.uploadProgress === 'object') {
            uploader.value.uploadProgress.value = 0;
        }
        const signResponses = await uploader.value.sign();
        emit('uploading');
        await uploader.value.store(signResponses);
        // Merge additional payload if provided
        const finalPayload = payload ? { ...payload, ...props.additionalPayload } : props.additionalPayload;
        const uploadResponses = await uploader.value.upload(signResponses, finalPayload);
        if (props.entity === 'staff') {
            for (const response of uploadResponses) {
                if (response.data?.file) await createDocumentExpiry(response.data.file, reminderDraft);
            }
        }
        await getAttachments();
        for (const response of uploadResponses) {
            const saved = attachments.value?.find((file) => file.id === response.data?.file?.id);
            if (saved) Object.assign(saved, response.data.file);
        }
        expiryDraft.value.enabled = false;
        if (reset) resetForm({ values: {} });
        if (props.multiple) {
            emit(
                'uploaded',
                uploadResponses.map((r) => r.data),
            );
        } else {
            emit('uploaded', uploadResponses[0].data);
        }
    } catch (error) {
        console.log('Error with upload: ', error);

        setErrors(error.errors);
        uploadErrors.value = Object.values(error.errors ?? {}).flat();
        // Normalize error payload for parent components (prefer field errors, then backend message)
        let resp = error?.response?.data || error?.data;
        if (typeof resp === 'string') {
            try {
                resp = JSON.parse(resp);
            } catch {
                // keep as string
            }
        }
        const combinedErrorsObj = {
            ...(resp?.errors && typeof resp.errors === 'object' ? resp.errors : {}),
            ...(error?.errors && typeof error.errors === 'object' ? error.errors : {}),
        };
        const fieldErrors = Object.values(combinedErrorsObj).flat().filter(Boolean);
        const message =
            (fieldErrors.length ? fieldErrors.join(' ') : undefined) ||
            resp?.message ||
            error?.message ||
            resp?.error ||
            error?.error ||
            'Upload failed.';
        emit('error', {
            message,
            original: error,
            response: error?.response,
            data: resp,
            errors: combinedErrorsObj,
        });
    }
}

const customProperties = computed(() => {
    return {
        expiry_date: {
            value: documentExpiry.value ? dayjs(documentExpiry.value).format('YYYY-MM-DD') : null,
            required: true,
            label: 'Expires',
            type: 'date',
        },
        check_date: {
            value: checkDate.value ? dayjs(checkDate.value).format('YYYY-MM-DD') : null,
            required: true,
            label: 'Date',
            type: 'date',
        },
        credit_score: {
            value: creditScore.value,
            required: true,
            label: 'Score',
            type: 'number',
        },
    };
});

function getCustomProperties() {
    let properties = {};
    let valid = true;
    Object.entries(customProperties.value).forEach(([property, options]) => {
        const collection = collectionOptions.value?.find((el) => el.id == values.collection_name);
        if (collection && collection[property]) {
            if ((options.required && options.value) || !options.required) {
                properties = { ...properties, [property]: options.value };
            } else {
                valid = false;
            }
        }
    });

    if (!(properties && Object.keys(properties).length > 0)) {
        properties = null;
    }
    return { properties, valid };
}

defineExpose({
    getAttachments,
    uploadHandler,
    files,
});
</script>
<style>
.list-enter-active,
.list-leave-active {
    transition: all 0.5s ease;
}
.list-enter-from,
.list-leave-to {
    opacity: 0;
    transform: translateX(30px);
}
</style>
